import { WelcomeScreen } from "@excalidraw/excalidraw";
import { Gallery } from "@excalidraw-ms/shared-ui";
import type { GalleryTab } from "@excalidraw-ms/shared-ui";
import { useCallback, useEffect, useState } from "react";
import type React from "react";
import type { AiGrantAccess } from "@excalidraw-ms/shared";
import BrandIcon from "./BrandIcon";
import type { DrawingItem } from "../hooks/useGalleryData";
import GallerySharingMetadata from "./GallerySharingMetadata";
import GalleryCardMenu from "./GalleryCardMenu";
import { AiIntroduction, type AiIntroductionProps } from "./AiIntroduction";

interface CustomWelcomeScreenProps {
  aiIntroduction?: AiIntroductionProps;
  /** Gallery data — when provided, a tabbed gallery renders below the center menu. */
  galleryTabs?: GalleryTab[];
  galleryActiveTab?: string;
  onGalleryTabChange?: (tabId: string) => void;
  galleryItems?: DrawingItem[];
  onGalleryItemClick?: (item: DrawingItem) => void;
  onGalleryItemDelete?: (item: DrawingItem) => void;
  onGalleryItemDuplicate?: (item: DrawingItem) => void;
  onGalleryItemRetryCopy?: (item: DrawingItem) => void;
  onGalleryItemDismissCopy?: (item: DrawingItem) => void;
  /**
   * Open access-only sharing without opening the drawing or joining a session.
   */
  onGalleryItemShare?: (item: DrawingItem) => void;
  /** Rename the drawing's title in place; offered to owners and editors. */
  onGalleryItemRename?: (item: DrawingItem) => void;
  aiStatusKnownDrawingIds?: Set<string>;
  /** Drawing ids that carry an active AI grant, for the AI badge on other tabs. */
  aiGrantedDrawingIds?: Set<string>;
  /** Caller-owned grants used for the persistent AI sharing indicator. */
  aiGrantsByDrawingId?: Map<string, { code: string; access: AiGrantAccess | null }>;
  galleryLoading?: boolean;
  galleryLoadingMore?: boolean;
  galleryHasMore?: boolean;
  onGalleryLoadMore?: () => void;
  onGalleryItemVisible?: (item: DrawingItem) => void;
  galleryError?: string | null;
  onGalleryRetry?: () => void;
}

/**
 * Custom <WelcomeScreen> child component for the Excalidraw editor.
 * Shown on empty canvas — provides branded experience with quick actions.
 * When gallery data is provided (user is signed in + entitled), a tabbed
 * gallery of recent/shared drawings appears below the center menu.
 *
 * The Cosmos gallery is the cloud-open experience. The center menu retains
 * local file import and help.
 */
export default function CustomWelcomeScreen({
  aiIntroduction,
  galleryTabs,
  galleryActiveTab,
  onGalleryTabChange,
  galleryItems,
  onGalleryItemClick,
  onGalleryItemDelete,
  onGalleryItemDuplicate,
  onGalleryItemRetryCopy,
  onGalleryItemDismissCopy,
  onGalleryItemShare,
  onGalleryItemRename,
  aiStatusKnownDrawingIds,
  aiGrantedDrawingIds,
  aiGrantsByDrawingId,
  galleryLoading,
  galleryLoadingMore,
  galleryHasMore,
  onGalleryLoadMore,
  onGalleryItemVisible,
  galleryError,
  onGalleryRetry,
}: CustomWelcomeScreenProps) {
  const [galleryWidth, setGalleryWidth] = useState<number>();
  const [introductionVisibility, setIntroductionVisibility] = useState<{
    accountId: string;
    visible: boolean;
  }>();
  const introductionAccountId = aiIntroduction?.accountId;
  const hasIntroduction =
    Boolean(aiIntroduction) &&
    introductionVisibility?.accountId === introductionAccountId &&
    introductionVisibility?.visible === true;
  const handleIntroductionVisibilityChange = useCallback(
    (visible: boolean) => {
      if (introductionAccountId === undefined) return;
      setIntroductionVisibility((previous) =>
        previous?.accountId === introductionAccountId && previous.visible === visible
          ? previous
          : { accountId: introductionAccountId, visible },
      );
    },
    [introductionAccountId],
  );
  const showGallery =
    (galleryTabs?.length ?? 0) > 0 && Boolean(galleryActiveTab) && Boolean(onGalleryTabChange);

  // Toggle a data attribute on the .excalidraw root so CSS can switch between
  // the upstream centered welcome layout (no gallery) and our top-anchored
  // layout (gallery present). Without this the logo collides with the
  // top-of-canvas toolbar hint.
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(".excalidraw");
    if (!root) return;
    if (showGallery || hasIntroduction) {
      root.setAttribute("data-welcome-gallery", "true");
    } else {
      root.removeAttribute("data-welcome-gallery");
    }
    return () => {
      root.removeAttribute("data-welcome-gallery");
    };
  }, [showGallery, hasIntroduction]);

  return (
    <WelcomeScreen>
      <WelcomeScreen.Center>
        <div
          className="excalidraw-ms-welcome-content"
          style={{
            ...welcomeContentStyle,
            ...(galleryWidth === undefined ? {} : { "--ai-gallery-width": `${galleryWidth}px` }),
          }}
        >
          <div className="excalidraw-ms-welcome-core" style={welcomeCoreStyle}>
            <WelcomeScreen.Center.Logo>
              <div style={logoContainerStyle}>
                <ExcalidrawMsLogo />
              </div>
            </WelcomeScreen.Center.Logo>

            <WelcomeScreen.Center.Heading>
              Excalidraw for Microsoft 365
            </WelcomeScreen.Center.Heading>

            <WelcomeScreen.Center.Menu>
              <WelcomeScreen.Center.MenuItemLoadScene />

              <WelcomeScreen.Center.MenuItemHelp />
            </WelcomeScreen.Center.Menu>
          </div>

          {aiIntroduction && (
            <AiIntroduction
              {...aiIntroduction}
              onVisibilityChange={handleIntroductionVisibilityChange}
            />
          )}
          {/* Gallery section — inside Center so it gets tunneled to the visible overlay.
              The .welcome-screen-center parent has pointer-events:none,
              so we need pointer-events:auto on our interactive content. */}
          {showGallery && (
            <div className="excalidraw-ms-gallery-section" style={gallerySectionStyle}>
              {galleryError && (
                <div style={galleryErrorStyle} role="alert" data-testid="gallery-error">
                  <span>{galleryError}</span>
                  {onGalleryRetry && (
                    <button onClick={onGalleryRetry} style={galleryRetryBtnStyle}>
                      Try again
                    </button>
                  )}
                </div>
              )}
              <Gallery<DrawingItem>
                tabs={galleryTabs!}
                activeTab={galleryActiveTab!}
                onTabChange={onGalleryTabChange!}
                items={galleryItems ?? []}
                onItemClick={onGalleryItemClick}
                loading={galleryLoading}
                loadingMore={galleryLoadingMore}
                hasMore={galleryHasMore}
                onLoadMore={onGalleryLoadMore}
                onItemVisible={onGalleryItemVisible}
                skeletonCount={6}
                cardWidth={240}
                onContentWidthChange={setGalleryWidth}
                renderCardContent={(item) => (
                  <GallerySharingMetadata
                    lastModifiedAt={item.lastModifiedAt}
                    sharedWithPeople={item.role !== "owner" ? true : item.sharedWithPeople}
                    sharedWithAi={
                      item.aiGrantCode ||
                      aiGrantedDrawingIds?.has(item.drawingId) ||
                      aiGrantsByDrawingId?.has(item.drawingId)
                        ? true
                        : aiStatusKnownDrawingIds?.has(item.drawingId)
                          ? false
                          : undefined
                    }
                  />
                )}
                getCardStatusRetry={(item) =>
                  item.cardStatus?.kind === "error" && item.copyRetryable && onGalleryItemRetryCopy
                    ? () => onGalleryItemRetryCopy(item)
                    : undefined
                }
                onCardStatusDismiss={onGalleryItemDismissCopy}
                renderCardBadge={(item) => {
                  return (
                    <span style={badgeRowStyle}>
                      {item.role !== "owner" && !item.aiGrantCode && (
                        <span style={sharedBadgeStyle}>{item.role}</span>
                      )}
                    </span>
                  );
                }}
                renderCardActions={(item) => {
                  const canShare = Boolean(onGalleryItemShare) && Boolean(item.drawingId);
                  // The "Shared with AI" tab lists grants, not drawings — there
                  // is nothing there to delete.
                  const canDelete =
                    galleryActiveTab !== "ai" &&
                    item.role === "owner" &&
                    Boolean(onGalleryItemDelete);
                  const canDuplicate =
                    galleryActiveTab === "recent" &&
                    item.role === "owner" &&
                    Boolean(onGalleryItemDuplicate);
                  const canRename =
                    Boolean(onGalleryItemRename) &&
                    Boolean(item.drawingId) &&
                    !item.aiGrantMissingDrawing &&
                    (item.role === "owner" || item.role === "editor");
                  if (item.cardStatus || (!canShare && !canRename && !canDelete && !canDuplicate))
                    return null;
                  return (
                    <span style={cardMenuRowStyle}>
                      <GalleryCardMenu
                        title={item.title}
                        onShare={canShare ? () => onGalleryItemShare!(item) : undefined}
                        shareTestId={`share-${item.drawingId}`}
                        onRename={canRename ? () => onGalleryItemRename!(item) : undefined}
                        onMakeCopy={canDuplicate ? () => onGalleryItemDuplicate!(item) : undefined}
                        onDelete={canDelete ? () => onGalleryItemDelete!(item) : undefined}
                      />
                    </span>
                  );
                }}
              />
            </div>
          )}
        </div>
      </WelcomeScreen.Center>

      {/* Hints overlap with the gallery grid (toolbar at top, menu on the
          left, help bottom-right), so we suppress all of them whenever the
          gallery is visible. When the gallery is hidden we render the
          standard upstream hints. */}
      {!showGallery && !hasIntroduction && (
        <>
          <WelcomeScreen.Hints.ToolbarHint>
            <p>Shape and drawing tools</p>
          </WelcomeScreen.Hints.ToolbarHint>

          <WelcomeScreen.Hints.MenuHint>
            <p>Save, export, and more</p>
          </WelcomeScreen.Hints.MenuHint>

          <WelcomeScreen.Hints.HelpHint>
            <p>Keyboard shortcuts and help</p>
          </WelcomeScreen.Hints.HelpHint>
        </>
      )}
    </WelcomeScreen>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

const ExcalidrawMsLogo = () => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "center" }}
    aria-label="Excalidraw for Microsoft 365"
  >
    <BrandIcon size={56} />
    <span
      style={{
        // Dedicated wordmark @font-face declared in index.html (aliases
        // Excalifont-Regular Latin subset with font-display: block) — no
        // fallback swap on first paint. Matches excalidraw.com's logo font.
        fontFamily: '"ExcalidrawMSWordmark", Excalifont, system-ui, sans-serif',
        fontSize: 36,
        fontWeight: 400,
        color: "var(--color-logo-text)",
        textTransform: "uppercase",
        letterSpacing: "-0.5px",
      }}
    >
      Excalidraw-MS
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const logoContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 16,
};

const welcomeContentStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  width: "100%",
  maxWidth: 1120,
  maxHeight: "var(--excalidraw-ms-welcome-max-height, 100%)",
  overflow: "hidden",
  margin: "0 auto",
};

const welcomeCoreStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
};

const gallerySectionStyle: React.CSSProperties = {
  pointerEvents: "auto",
  flex: "1 1 auto",
  minHeight: 0,
  width: "100%",
  maxWidth: 1120,
  maxHeight: "none",
  boxSizing: "border-box",
  scrollbarGutter: "stable both-edges",
  overflowY: "auto",
  margin: "0 auto",
  padding: "0 8px",
};

const galleryErrorStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "8px 12px",
  marginBottom: 12,
  borderRadius: 8,
  background: "var(--exms-danger-soft)",
  color: "var(--exms-danger-text)",
  fontSize: 13,
};

const galleryRetryBtnStyle: React.CSSProperties = {
  padding: "4px 10px",
  borderRadius: 6,
  border: "1px solid var(--color-danger)",
  background: "transparent",
  color: "var(--exms-danger-text)",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const sharedBadgeStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  padding: "2px 6px",
  borderRadius: 4,
  background: "var(--exms-info-soft)",
  color: "var(--exms-info-text)",
};

const badgeRowStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

const cardMenuRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
};
